import React, { useState } from 'react';
import { X, CreditCard } from 'lucide-react';
interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (paymentData: PaymentData) => void;
  currentCard?: {
    type: string;
    last4: string;
    expiry: string;
  };
}
export interface PaymentData {
  cardNumber: string;
  cardName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}
export function PaymentMethodModal({
  isOpen,
  onClose,
  onSave,
  currentCard
}: PaymentMethodModalProps) {
  const [formData, setFormData] = useState<PaymentData>({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [errors, setErrors] = useState<Partial<PaymentData>>({});
  if (!isOpen) return null;
  const handleChange = (field: keyof PaymentData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ').substring(0, 19); // Max 16 digits + 3 spaces
  };
  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value.replace(/\D/g, ''));
    handleChange('cardNumber', formatted);
  };
  const validate = () => {
    const newErrors: Partial<PaymentData> = {};
    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Valid card number required';
    }
    if (!formData.cardName.trim()) {
      newErrors.cardName = 'Cardholder name required';
    }
    if (!formData.expiryMonth || parseInt(formData.expiryMonth) < 1 || parseInt(formData.expiryMonth) > 12) {
      newErrors.expiryMonth = 'Valid month required';
    }
    if (!formData.expiryYear || formData.expiryYear.length !== 2) {
      newErrors.expiryYear = 'Valid year required';
    }
    if (!formData.cvv || formData.cvv.length < 3) {
      newErrors.cvv = 'Valid CVV required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-2989">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A] bg-opacity-50" onClick={onClose} data-id="element-2990" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-id="element-2991">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]" data-id="element-2992">
          <div className="flex items-center gap-3" data-id="element-2993">
            <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-2994">
              <CreditCard className="w-5 h-5 text-[#0EA5E9]" data-id="element-2995" />
            </div>
            <h2 className="text-xl font-semibold text-[#0F172A]" data-id="element-2996">
              Update Payment Method
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569] transition-colors" data-id="element-2997">
            <X className="w-5 h-5" data-id="element-2998" />
          </button>
        </div>

        {/* Current Card Info */}
        {currentCard && <div className="p-6 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-2999">
            <div className="text-sm text-[#475569] mb-2" data-id="element-3000">Current Card</div>
            <div className="flex items-center gap-3" data-id="element-3001">
              <CreditCard className="w-5 h-5 text-[#94A3B8]" data-id="element-3002" />
              <div data-id="element-3003">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-3004">
                  {currentCard.type} ending in {currentCard.last4}
                </div>
                <div className="text-xs text-[#94A3B8]" data-id="element-3005">
                  Expires {currentCard.expiry}
                </div>
              </div>
            </div>
          </div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-3006">
          {/* Card Number */}
          <div data-id="element-3007">
            <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-3008">
              Card Number
            </label>
            <input type="text" value={formData.cardNumber} onChange={e => handleCardNumberChange(e.target.value)} placeholder="1234 5678 9012 3456" className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cardNumber ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3009" />
            {errors.cardNumber && <div className="text-sm text-[#EF4444] mt-1" data-id="element-3010">
                {errors.cardNumber}
              </div>}
          </div>

          {/* Cardholder Name */}
          <div data-id="element-3011">
            <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-3012">
              Cardholder Name
            </label>
            <input type="text" value={formData.cardName} onChange={e => handleChange('cardName', e.target.value)} placeholder="Name on card" className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cardName ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3013" />
            {errors.cardName && <div className="text-sm text-[#EF4444] mt-1" data-id="element-3014">
                {errors.cardName}
              </div>}
          </div>

          {/* Expiry and CVV */}
          <div className="grid grid-cols-3 gap-4" data-id="element-3015">
            {/* Expiry Month */}
            <div data-id="element-3016">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-3017">
                Month
              </label>
              <input type="text" value={formData.expiryMonth} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 2);
              handleChange('expiryMonth', value);
            }} placeholder="MM" maxLength={2} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.expiryMonth ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3018" />
              {errors.expiryMonth && <div className="text-xs text-[#EF4444] mt-1" data-id="element-3019">Required</div>}
            </div>

            {/* Expiry Year */}
            <div data-id="element-3020">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-3021">
                Year
              </label>
              <input type="text" value={formData.expiryYear} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 2);
              handleChange('expiryYear', value);
            }} placeholder="YY" maxLength={2} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.expiryYear ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3022" />
              {errors.expiryYear && <div className="text-xs text-[#EF4444] mt-1" data-id="element-3023">Required</div>}
            </div>

            {/* CVV */}
            <div data-id="element-3024">
              <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-3025">
                CVV
              </label>
              <input type="text" value={formData.cvv} onChange={e => {
              const value = e.target.value.replace(/\D/g, '').substring(0, 4);
              handleChange('cvv', value);
            }} placeholder="123" maxLength={4} className={`w-full h-12 px-4 border rounded-md bg-white text-[#0F172A] text-[15px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all ${errors.cvv ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3026" />
              {errors.cvv && <div className="text-xs text-[#EF4444] mt-1" data-id="element-3027">Required</div>}
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-[#F0F9FF] border border-[#BFDBFE] rounded-md p-3" data-id="element-3028">
            <div className="text-xs text-[#1E40AF]" data-id="element-3029">
              🔒 Your payment information is encrypted and secure. We never
              store your full card details.
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4" data-id="element-3030">
            <button type="button" onClick={onClose} className="flex-1 h-12 px-6 border border-[#CBD5E1] rounded-md text-[#475569] font-medium hover:bg-[#F1F5F9] transition-colors" data-id="element-3031">
              Cancel
            </button>
            <button type="submit" className="flex-1 h-12 px-6 bg-[#0EA5E9] text-white font-medium rounded-md hover:bg-[#0284C7] active:scale-[0.98] transition-all" data-id="element-3032">
              Update Card
            </button>
          </div>
        </form>
      </div>
    </div>;
}